import { Controller } from '@hotwired/stimulus'
import { googleMapsCenterEmbedUrl } from '../lib/google_maps_link'

const DEFAULT_CENTER = { lat: 31.5204, lng: 74.3587 }
const DEFAULT_ZOOM = 17

// Google Maps embed + realtime CSS-pan drag + live GPS blue dot / recenter button.
export default class extends Controller {
  static targets = [
    'modal', 'addressInput', 'latInput', 'lngInput', 'addressLabel',
    'mapPreview', 'mapPan', 'status', 'dragSurface', 'liveDot', 'liveButton'
  ]
  static values = { reverseUrl: String }

  connect () {
    this.pending = null
    this.live = null
    this.geocodeSeq = 0
    this.dragging = false
    this.watchId = null
    this.modalEl = this.hasModalTarget ? this.modalTarget : null
    if (!this.modalEl) return

    this.cacheModalParts()
    this.moveModalToBody()
    this.bindModalClicks()
    this.bindDrag()
  }

  disconnect () {
    this.stopLiveWatch()
    this.unbindModalClicks()
    this.unbindDrag()
    this.unbindMapLoad()
    this.hideModal()
    this.returnModalHome()
  }

  cacheModalParts () {
    this.addressLabelEl = this.modalEl.querySelector('[data-delivery-location-picker-target="addressLabel"]')
    this.mapPreviewEl = this.modalEl.querySelector('[data-delivery-location-picker-target="mapPreview"]')
    this.mapPanEl = this.modalEl.querySelector('[data-delivery-location-picker-target="mapPan"]')
    this.statusEl = this.modalEl.querySelector('[data-delivery-location-picker-target="status"]')
    this.dragSurfaceEl = this.modalEl.querySelector('[data-delivery-location-picker-target="dragSurface"]')
    this.liveDotEl = this.modalEl.querySelector('[data-delivery-location-picker-target="liveDot"]')
    this.liveButtonEl = this.modalEl.querySelector('[data-delivery-location-picker-target="liveButton"]')
  }

  moveModalToBody () {
    if (!this.modalEl || this.modalEl.parentElement === document.body) return
    this.modalHome = this.modalEl.parentElement
    this.modalNextSibling = this.modalEl.nextElementSibling
    document.body.appendChild(this.modalEl)
  }

  returnModalHome () {
    if (!this.modalEl || !this.modalHome) return
    if (!document.body.contains(this.modalEl)) {
      this.modalHome = null
      this.modalNextSibling = null
      return
    }
    if (this.modalNextSibling && this.modalNextSibling.parentElement === this.modalHome) {
      this.modalHome.insertBefore(this.modalEl, this.modalNextSibling)
    } else {
      this.modalHome.appendChild(this.modalEl)
    }
    this.modalHome = null
    this.modalNextSibling = null
  }

  bindModalClicks () {
    if (!this.modalEl || this.onModalClick) return
    this.onModalClick = (event) => {
      const button = event.target.closest('[data-delivery-modal-action]')
      if (!button || !this.modalEl.contains(button)) return
      event.preventDefault()
      const action = button.getAttribute('data-delivery-modal-action')
      if (action === 'close') this.close()
      else if (action === 'useCurrentLocation' || action === 'goToLive') this.goToLiveLocation()
      else if (action === 'confirm') this.confirm()
    }
    this.modalEl.addEventListener('click', this.onModalClick)
  }

  unbindModalClicks () {
    if (!this.modalEl || !this.onModalClick) return
    this.modalEl.removeEventListener('click', this.onModalClick)
    this.onModalClick = null
  }

  bindDrag () {
    if (!this.dragSurfaceEl || this.onPointerDown) return
    this.onPointerDown = (e) => this.startDrag(e)
    this.onPointerMove = (e) => this.moveDrag(e)
    this.onPointerUp = (e) => this.endDrag(e)
    this.dragSurfaceEl.addEventListener('pointerdown', this.onPointerDown)
    this.dragSurfaceEl.addEventListener('pointermove', this.onPointerMove)
    this.dragSurfaceEl.addEventListener('pointerup', this.onPointerUp)
    this.dragSurfaceEl.addEventListener('pointercancel', this.onPointerUp)
  }

  unbindDrag () {
    if (!this.dragSurfaceEl || !this.onPointerDown) return
    this.dragSurfaceEl.removeEventListener('pointerdown', this.onPointerDown)
    this.dragSurfaceEl.removeEventListener('pointermove', this.onPointerMove)
    this.dragSurfaceEl.removeEventListener('pointerup', this.onPointerUp)
    this.dragSurfaceEl.removeEventListener('pointercancel', this.onPointerUp)
    this.onPointerDown = null
    this.onPointerMove = null
    this.onPointerUp = null
  }

  unbindMapLoad () {
    if (!this.mapPreviewEl || !this.onMapLoad) return
    this.mapPreviewEl.removeEventListener('load', this.onMapLoad)
    this.onMapLoad = null
  }

  open (event) {
    event?.preventDefault()
    if (!this.modalEl) return

    this.moveModalToBody()
    this.modalEl.classList.remove('hidden')
    document.body.classList.add('overflow-hidden')
    this.resetMapPan()
    this.startLiveWatch()

    if (!this.pending) {
      this.applyCoords(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, { quiet: true })
      this.setStatus('Drag the map, or tap the blue location button.')
    } else {
      this.renderMap(this.pending.lat, this.pending.lng)
      this.updateLiveDot()
    }
  }

  close (event) {
    event?.preventDefault()
    this.hideModal()
  }

  hideModal () {
    if (!this.modalEl) return
    this.modalEl.classList.add('hidden')
    document.body.classList.remove('overflow-hidden')
    this.stopLiveWatch()
    this.resetMapPan()
  }

  startLiveWatch () {
    if (!navigator.geolocation || this.watchId != null) return

    this.watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.live = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        }
        this.updateLiveDot()
        this.liveButtonEl?.classList.remove('opacity-40')
        this.liveButtonEl?.removeAttribute('disabled')
      },
      () => {
        this.liveButtonEl?.classList.add('opacity-40')
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 20000 }
    )
  }

  stopLiveWatch () {
    if (this.watchId == null) return
    navigator.geolocation.clearWatch(this.watchId)
    this.watchId = null
  }

  async goToLiveLocation () {
    if (this.live) {
      this.resetMapPan()
      await this.applyCoords(this.live.lat, this.live.lng)
      this.setStatus('Moved to your live location. Drag to fine-tune, then confirm.')
      return
    }

    if (!navigator.geolocation) {
      this.setStatus('Live location is not available on this device.')
      return
    }

    this.setStatus('Getting live location…')
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        this.live = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy
        }
        this.updateLiveDot()
        this.resetMapPan()
        await this.applyCoords(this.live.lat, this.live.lng)
        this.setStatus('Moved to your live location. Drag to fine-tune, then confirm.')
      },
      () => this.setStatus('Location permission denied. Allow location access and try again.'),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    )
  }

  startDrag (event) {
    if (event.button != null && event.button !== 0) return
    if (!this.pending) {
      this.applyCoords(DEFAULT_CENTER.lat, DEFAULT_CENTER.lng, { quiet: true })
    }

    this.dragging = true
    this.dragStartX = event.clientX
    this.dragStartY = event.clientY
    this.dragStartLat = this.pending.lat
    this.dragStartLng = this.pending.lng
    this.dragSurfaceEl.setPointerCapture?.(event.pointerId)
    this.dragSurfaceEl.classList.add('cursor-grabbing')
    event.preventDefault()
  }

  moveDrag (event) {
    if (!this.dragging || !this.pending) return

    const dx = event.clientX - this.dragStartX
    const dy = event.clientY - this.dragStartY
    const next = this.offsetFromDrag(this.dragStartLat, this.dragStartLng, dx, dy)
    this.pending = { ...this.pending, lat: next.lat, lng: next.lng }

    this.setMapPan(dx, dy)
    this.updateLiveDot()
    this.setStatus('Release to lock address…')
  }

  async endDrag (event) {
    if (!this.dragging) return
    this.dragging = false
    this.dragSurfaceEl.classList.remove('cursor-grabbing')
    try {
      this.dragSurfaceEl.releasePointerCapture?.(event.pointerId)
    } catch (_error) {}

    if (!this.pending) return

    const { lat, lng } = this.pending
    // Keep CSS pan until new map loads - prevents blink + jump.
    await this.renderMapAfterPan(lat, lng)
    this.updateLiveDot()
    await this.refreshAddress(lat, lng)
  }

  renderMapAfterPan (lat, lng) {
    return new Promise((resolve) => {
      if (!this.mapPreviewEl) {
        this.resetMapPan()
        resolve()
        return
      }

      const next = googleMapsCenterEmbedUrl(lat, lng, DEFAULT_ZOOM)
      const current = this.mapPreviewEl.getAttribute('src') || ''
      if (current === next) {
        this.resetMapPan()
        resolve()
        return
      }

      this.unbindMapLoad()
      let settled = false
      const finish = () => {
        if (settled) return
        settled = true
        this.unbindMapLoad()
        this.resetMapPan()
        resolve()
      }

      this.onMapLoad = () => finish()
      this.mapPreviewEl.addEventListener('load', this.onMapLoad)
      this.mapPreviewEl.src = next
      window.setTimeout(finish, 1200)
    })
  }

  setMapPan (dx, dy) {
    if (!this.mapPanEl) return
    this.mapPanEl.style.transform = `translate3d(calc(-50% + ${dx}px), calc(-50% + ${dy}px), 0)`
  }

  resetMapPan () {
    if (!this.mapPanEl) return
    this.mapPanEl.style.transform = 'translate3d(-50%, -50%, 0)'
  }

  offsetFromDrag (lat, lng, dx, dy) {
    // Drag map right → center moves west. Drag down → center moves north.
    const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / (2 ** DEFAULT_ZOOM)
    const deltaLat = (dy * metersPerPixel) / 111_320
    const cosLat = Math.cos((lat * Math.PI) / 180) || 1e-6
    const deltaLng = -(dx * metersPerPixel) / (111_320 * cosLat)
    return {
      lat: Math.max(-85, Math.min(85, lat + deltaLat)),
      lng: ((lng + deltaLng + 540) % 360) - 180
    }
  }

  updateLiveDot () {
    if (!this.liveDotEl || !this.live || !this.pending) {
      this.liveDotEl?.classList.add('hidden')
      return
    }

    const { dx, dy } = this.latLngToPixels(this.live.lat, this.live.lng, this.pending.lat, this.pending.lng)
    const mapBox = this.dragSurfaceEl?.getBoundingClientRect()
    if (mapBox) {
      const halfW = mapBox.width / 2
      const halfH = mapBox.height / 2
      if (Math.abs(dx) > halfW * 1.4 || Math.abs(dy) > halfH * 1.4) {
        this.liveDotEl.classList.add('hidden')
        return
      }
    }

    this.liveDotEl.classList.remove('hidden')
    this.liveDotEl.style.transform = `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px))`
  }

  latLngToPixels (lat, lng, centerLat, centerLng) {
    const metersPerPixel = (156543.03392 * Math.cos((centerLat * Math.PI) / 180)) / (2 ** DEFAULT_ZOOM)
    const dy = -((lat - centerLat) * 111_320) / metersPerPixel
    const cosLat = Math.cos((centerLat * Math.PI) / 180) || 1e-6
    const dx = ((lng - centerLng) * 111_320 * cosLat) / metersPerPixel
    return { dx, dy }
  }

  async applyCoords (lat, lng, { quiet = false } = {}) {
    this.renderMap(lat, lng)
    if (quiet) {
      this.pending = { lat, lng, label: this.coordinateLabel(lat, lng) }
      if (this.addressLabelEl) this.addressLabelEl.textContent = this.pending.label
      this.updateLiveDot()
      return
    }
    await this.refreshAddress(lat, lng)
  }

  async refreshAddress (lat, lng) {
    const seq = ++this.geocodeSeq
    this.setStatus('Updating address…')
    const label = await this.reverseGeocode(lat, lng)
    if (seq !== this.geocodeSeq) return

    this.pending = { lat, lng, label }
    if (this.addressLabelEl) this.addressLabelEl.textContent = label
    this.updateLiveDot()
    this.setStatus('Pin set. Drag to adjust, or tap blue location for live GPS.')
  }

  renderMap (lat, lng) {
    if (!this.mapPreviewEl) return
    const next = googleMapsCenterEmbedUrl(lat, lng, DEFAULT_ZOOM)
    if (this.mapPreviewEl.getAttribute('src') !== next) {
      this.mapPreviewEl.src = next
    }
  }

  confirm () {
    if (!this.pending) {
      this.setStatus('Set a location first.')
      return
    }

    const addressInput = this.addressField()
    const latInput = this.latField()
    const lngInput = this.lngField()
    if (!addressInput || !latInput || !lngInput) {
      this.setStatus('Could not save location. Refresh and try again.')
      return
    }

    const { lat, lng, label } = this.pending
    addressInput.value = this.confirmedAddressLabel(label)
    latInput.value = Number(lat).toFixed(7)
    lngInput.value = Number(lng).toFixed(7)
    addressInput.dispatchEvent(new Event('input', { bubbles: true }))
    addressInput.dispatchEvent(new Event('change', { bubbles: true }))
    this.hideModal()
  }

  addressField () {
    return this.element.querySelector('#checkout_delivery_address') ||
      (this.hasAddressInputTarget ? this.addressInputTarget : null)
  }

  latField () {
    return this.element.querySelector('[name="delivery_latitude"]') ||
      (this.hasLatInputTarget ? this.latInputTarget : null)
  }

  lngField () {
    return this.element.querySelector('[name="delivery_longitude"]') ||
      (this.hasLngInputTarget ? this.lngInputTarget : null)
  }

  async reverseGeocode (lat, lng) {
    if (!this.hasReverseUrlValue) return this.coordinateLabel(lat, lng)
    try {
      const response = await fetch(`${this.reverseUrlValue}?${new URLSearchParams({ lat, lng })}`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      })
      const payload = await response.json()
      return payload.ok && payload.label ? payload.label : this.coordinateLabel(lat, lng)
    } catch (_error) {
      return this.coordinateLabel(lat, lng)
    }
  }

  coordinateLabel (lat, lng) {
    return `${Number(lat).toFixed(7)}, ${Number(lng).toFixed(7)}`
  }

  confirmedAddressLabel (fallbackLabel) {
    const addressInput = this.addressField()
    const typed = addressInput?.value?.trim()
    if (!typed) return fallbackLabel

    const currentMapLabel = this.pending?.label?.trim()
    if (!currentMapLabel) return typed

    return typed === currentMapLabel ? fallbackLabel : typed
  }

  setStatus (message) {
    if (this.statusEl) this.statusEl.textContent = message
  }
}
